import type { SsrProps } from '@/core/features/app/ssr-props';

import React from 'react';

import dynamic from 'next/dynamic';

import { NEXT_PUBLIC_CHECK24_DOMAIN } from '@/constants/env';
import { useMetaDataState } from '@/core/features/a-dynamic-page/hooks/meta-data-state';
import { useClientUrl, useLayoutId } from '@/core/features/app/app-atoms';
import { historyBack, isHistoryBackSupported } from '@/core/features/router/history';
import { useRouterLink } from '@/core/features/router/router-link';
import { WIREFRAME_SEARCH_OPEN_CLASS_MOBILE } from '@/core/features/wireframe/constants';
import CustomerSupportOverlay from '@/core/features/wireframe/customer-support-overlay';
import WireframeCssAssetMobile from '@/core/features/wireframe/generated/wireframe-css-asset-mobile';
import WireframeHeaderMobile from '@/core/features/wireframe/generated/wireframe-header-mobile';
import { classC24mSearchIcon, idC24mHeader } from '@/core/features/wireframe/generated/wireframe-html-selectors-mobile';
import WireframeJsAssetMobile from '@/core/features/wireframe/generated/wireframe-js-asset-mobile';
import { getURL } from '@/core/utils/url';

const WireframeHeaderNavigationMobile = dynamic(
    () =>
        import(
            /* webpackChunkName: "WireframeHeaderNavigationMobile" */ '@/core/features/wireframe/generated/wireframe-header-navigation-mobile'
        ),
);

const WireframeAppBannerWrapper = dynamic(
    () =>
        import(
            /* webpackChunkName: "WireframeAppBannerWrapper" */ '@/core/features/wireframe/wireframe-app-banner-wrapper'
        ),
);

export default function WireframeHeaderMobileWrapper({
    headerTitleComponent,
    ssrProps,
}: {
    headerTitleComponent: React.ReactNode;
    ssrProps: SsrProps;
}) {
    const [isMobileHeaderNavigationVisible, setMobileHeaderNavigationVisible] = React.useState(false);
    const wasMobileHeaderNavigationInitRef = React.useRef(false);

    const [clientUrl] = useClientUrl();
    const href = getURL(clientUrl).toString();
    const layoutId = useLayoutId();
    const meta = useMetaDataState();

    const { deviceoutput } = ssrProps.cookiesMapped;
    const isApp = deviceoutput === 'app';

    const wireframeBackVisible = !!meta?.wireframeBackUrl;
    const menuButtonVisible = layoutId?.includes('S0') ?? false;
    const appBannerVisible = layoutId?.includes('S0') ?? false;

    const onWireframeBack = useWireframeBack(meta?.wireframeBackUrl ?? null);
    useHideSearch();

    if (!NEXT_PUBLIC_CHECK24_DOMAIN) {
        return null;
    }

    if (isApp) {
        return (
            <WireframeJsAssetMobile
                deviceoutput={deviceoutput}
                layoutId={layoutId}
            />
        );
    }
    return (
        <>
            {appBannerVisible && <WireframeAppBannerWrapper {...ssrProps} />}
            <WireframeHeaderMobile
                assetComponent={
                    <>
                        <WireframeJsAssetMobile
                            deviceoutput={deviceoutput}
                            layoutId={layoutId}
                        />
                        <WireframeCssAssetMobile />
                    </>
                }
                check24Domain={NEXT_PUBLIC_CHECK24_DOMAIN}
                contactLayerComponent={<CustomerSupportOverlay />}
                href={href}
                loginStatus={ssrProps.user.loginStatus}
                menuButtonVisible={menuButtonVisible}
                navigationComponent={
                    isMobileHeaderNavigationVisible && (
                        <WireframeHeaderNavigationMobile
                            check24Domain={NEXT_PUBLIC_CHECK24_DOMAIN}
                            href={href}
                            loginStatus={ssrProps.user.loginStatus}
                            username={ssrProps.user.username}
                        />
                    )
                }
                onClickMenuButton={() => {
                    setMobileHeaderNavigationVisible(true);
                    if (wasMobileHeaderNavigationInitRef.current) {
                        return;
                    }
                    setTimeout(() => {
                        window.C24M?.navi.init();
                        window.C24M?.navi.open(); // init closes the menu
                        wasMobileHeaderNavigationInitRef.current = true;
                    }, 1200);
                }}
                onWireframeBack={onWireframeBack}
                titleComponent={headerTitleComponent}
                username={ssrProps.user.username}
                wireframeBackUrl={ssrProps.meta?.wireframeBackUrl ?? '/'}
                wireframeBackVisible={wireframeBackVisible}
            />
        </>
    );
}

const useWireframeBack = (wireframeBackUrl: null | string) => {
    const { navigate } = useRouterLink();

    const onWireframeBack = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();

        if (isHistoryBackSupported()) {
            historyBack();
            return;
        }

        if (wireframeBackUrl) {
            // routes external
            if (wireframeBackUrl.indexOf('http') === 0) {
                location.assign(wireframeBackUrl);
                return;
            }
            navigate(wireframeBackUrl, { historyState: { isBackNavigation: true } });
        }
    };

    return onWireframeBack;
};

/**
 * Effect: Hide opened search wehn route is changed
 */
const useHideSearch = () => {
    const [clientUrl] = useClientUrl();

    React.useEffect(() => {
        const mobileSearchIcon = document.querySelector(`.${classC24mSearchIcon}`);
        const mobileHeader = document.getElementById(`${idC24mHeader}`);

        if (!(mobileSearchIcon instanceof HTMLElement) || !(mobileHeader instanceof HTMLElement)) {
            return;
        }
        if (mobileHeader.classList.contains(WIREFRAME_SEARCH_OPEN_CLASS_MOBILE)) {
            mobileSearchIcon.click();
        }
    }, [clientUrl]);
};
